<template>
  <div :class="`scorecard scorecard--${layout}`" :style="{width: width}">
    <ScorecardHeader
      :topic="topic"
      :family="family"
      :policyType="policyType"
      :layout="layout === 'region' ? 'topic' : 'policyType'"
      :test="test"
    />
    <div class="scorecard-categories">
    <PolicyScores v-if="layout === 'region' || layout === 'combined'" :regionGroups="regionGroups" :policies="relevantPolicies" showRegions :showScores="false" @select:region="selectRegion">
        <!-- <ItemChart :inForce="getPolicyCount(regions, null, 'In force')" :announced="getPolicyCount(regions, null, 'Announced')" :total="regions.length"/> -->
    </PolicyScores>
    <template v-if="layout !== 'region'">
    <PolicyScores  v-for="category in categories"
        :key="category" :regionGroups="regionGroups" :policies="relevantPolicies" :category="category" showScores>
        <DataGetter
          :url="`${url}/dashboard`"
          :params="{
            topic,
            family: family !== 'All' ? family : null,
            policyType,
            category: category || null,
          }"
          v-slot="{ data }"
        >
          <BarChart
            v-if="data.length > 0 && !data[0].label.includes('spending') && policyType"
            :key="`${category}chart`"
            :data="data"
            :topic="topic"
            :family="family"
            :policyType="policyType"
            :policies="relevantPolicies"
            :category="category !== '' ? category : null"
          />
            <!-- <ItemChart v-for="group in regionGroups" :key="group.name" :inForce="getPolicyCount(group.regions, category, 'In force')" :total="group.regions.length"/> -->
        <ItemChart v-else :inForce="getPolicyCount(regions, category, 'In force')" :announced="getPolicyCount(regions, category, 'Announced')" :total="regions.length"/>
        </DataGetter>
        
    </PolicyScores>
    </template>
    </div>
  </div>
</template>

<script>
import { DataGetter } from '@international-energy-agency/vue-ui';
import ScorecardHeader from "./ScorecardHeader.vue";
import PolicyScores from "./PolicyScores.vue";
import ItemChart from '../charts/ItemChart.vue';
import BarChart from '../charts/BarChart.vue';
import tags from "@/assets/tags.json";
import g20 from "@/assets/g20.json";
import members from "@/assets/members.json";
import eu27 from '@/assets/eu27.json'
const CATEGORY_MIN_WIDTH = 132;
const SCORECARD_MIN_WIDTH = 280;
const MARGIN = 20;
const EU_MEMBERS = [...g20, ...members].filter(country => eu27.includes(country))
export default {
  props: {
    topic: {
      type: String,
      required: false,
    },
    family: {
      type: String,
      required: false,
    },
    policyType: {
      type: String,
      required: false,
    },
    regions: {
      type: Array,
      required: true,
    },
    policies: {
      type: Array,
      required: false,
    },
    url: {
      type: String,
      default: "https://api.iea.org/policies/v2",
    },
    layout: {
      type: String,
      validator: (value) =>
        ["region", "policyType", "combined"].includes(value),
      required: true,
    },
    /* If true, policy counts will take into account all countries, not just those shown */
    allCountries: {
        type: Boolean,
        default: false
    },
    test: {
      type: Boolean,
      default: false,
    },
  },
  components: { DataGetter, ScorecardHeader, PolicyScores, ItemChart, BarChart },
  methods: {
      getPolicyCount(regions, category, status = 'In force') {
          const { relevantPolicies } = this;
          const policies = {};
          const categoryPolicies = category ? relevantPolicies.filter(policy => policy.category.includes(category)) : relevantPolicies;
          policies['In force'] = regions.filter(region => categoryPolicies.some(policy => policy.country === region && policy.status === 'In force'));
          policies['Announced'] = regions.filter(region => categoryPolicies.some(policy => policy.country === region && !policies['In force'].includes(policy.country) && policy.status === 'Announced'));
          // if EU policy is included, don't double count EU members' policies
          if (policies[status].includes('European Union')) {
            policies[status] = policies[status].filter(region => !EU_MEMBERS.includes(region));
            policies[status].push(...EU_MEMBERS);
          }
          return policies[status].length
      },
      selectRegion(region) {
        this.$emit('select:region', region)
      }
  },
  computed: {
    width() {
      const { categories, layout } = this;
      return layout === 'combined' ? 'max-content' : `${(categories.length * CATEGORY_MIN_WIDTH) + MARGIN * 2 > SCORECARD_MIN_WIDTH ? (categories.length * CATEGORY_MIN_WIDTH) + MARGIN * 2 : SCORECARD_MIN_WIDTH}px`;
    },
    regionGroups() {
      return [
        {
          name: "G20 members",
          regions: g20,
          // regions: regions.filter((region) => g20.includes(region)),
        },
        {
          name: "Other regions",
          // regions: regions.filter((region) => !g20.includes(region)),
          regions: members,
        },
      ];
    },
    relevantPolicies() {
      const { topic, policyType, relevantCategories, regions, allCountries } = this;
      if (!this.policies) return [];
      return this.policies.filter(
        (policy) =>
          (allCountries || regions.includes(policy.country)) &&
          policy.topic.includes(topic) &&
          (!policyType || policy.type.includes(policyType)) &&
          (relevantCategories.length === 0 ||
            !relevantCategories[0] ||
            (policy.category &&
              policy.category.some((category) =>
                relevantCategories.includes(category)
              )))
      );
    },
    relevantCategories() {
      const { topic, policyType } = this;
      return tags.reduce(
        (categories, tag) =>
          tag.policyType === policyType &&
          tag.topic === topic &&
          !categories.includes(tag.category)
            ? [...categories, tag.category]
            : categories,
        []
      );
    },
    categories() {
      const { relevantCategories } = this;
      let categories = this.relevantPolicies.reduce((categories, policy) => {
        if (!policy.category) return categories;
        policy.category.forEach((category) => {
          if (
            relevantCategories.includes(category) &&
            !categories.includes(category)
          ) {
            categories.push(category);
          }
        });
        return categories;
      }, []);
      if (
        this.topic === "Power" &&
        this.policyType === "Government spending in low-carbon electricity"
      ) {
        categories = [];
      }
      return categories.length > 0
        ? categories.sort((a, b) => a.localeCompare(b))
        : [""];
    },
    inForcePoliciesByCountry() {
        const {relevantCategories, relevantPolicies} = this;
        return relevantCategories.reduce((categories, category) => {
            categories[category || 'All'] = relevantPolicies.reduce((countries, policy) => !countries.includes(policy.country) && policy.status === 'In force' && (!category || (policy.category && policy.category === category)) ?[...countries, policy.country] : countries, [])
            return categories;
        }, {})
    },
    inForcePolicies() {
        const { relevantPolicies, relevantCategories } = this;
        return relevantPolicies.filter(policy => policy.status === 'In force')
    }
  },
};
</script>

<style scoped>
.scorecard {
    min-width: 280px;
    margin-right: 32px;
    border: 1px solid #949494;
    border-radius: 16px;
    padding: 20px;
}
.scorecard--region {
    background: #f2f2f2;
    border: none;
}
.scorecard-categories {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2px;
}
@media screen and (max-width: 959px) {
 .scorecard {
  flex: 1 0 auto;
  padding-right: 24px;
 } 
}
</style>
